import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Grid, IconButton, Typography } from '@mui/material';
import { EIconTypes } from 'constants/Icons';
import { Loader } from 'components/atoms/Loader';
import {
  ControlButton,
  ControlCounter,
  GalleryCell,
  GalleryContainer,
  IconStyled,
  ImageContainer,
  ImagesGalleryContainer,
  ImagesGalleryControl,
  MainPhotoChips,
  StyledEmptyPhoto,
  StyledPhoto,
  ZoomIcon,
} from './styles';
import Icon from 'components/atoms/Icon/Icon';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { getGallery, getGalleryPreview } from 'requests/gallery';
import { ESize, IEntryData, IPhoto } from 'models/galery';
import { colors } from 'theme/colors';
import { ModalImages } from '../ModalImages';
import { EPermission } from 'models/permissions';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { EModuleType } from './helper';

interface IImagesGallery {
  moduleType: EModuleType;
  ownerId: string;
  buttonId?: string;
  showButton?: boolean;
  buttonPermission?: EPermission | EPermission[];
  onClickEdit?: () => void;
  onClickAdd?: () => void;
  init?: () => void;
  hidden?: boolean;
}

const ImagesGallery = ({
  moduleType,
  ownerId,
  buttonId,
  showButton = true,
  buttonPermission,
  onClickAdd,
  onClickEdit,
  init,
  hidden = false,
}: IImagesGallery) => {
  const { t } = useTranslations();
  const [moduleLabel, setModuleLabel] = useState<string>('');
  const [imageThumbnails, setImageThumbnails] = useState<IPhoto>({});
  const [galleryData, setGalleryData] = useState<IEntryData[]>([]);
  const [currentPosition, setCurrentPosition] = useState<number>(1);
  const [openFullSize, setOpenFullSize] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [imageThumbnailFileIsLoading, setImageThumbnailFileIsLoading] = useState(false);

  useEffect(() => {
    switch (moduleType) {
      case EModuleType.LE:
        setModuleLabel('manageImages.legalEntity.label');
        break;
      case EModuleType.FRT:
        setModuleLabel('manageImages.firearm.label');
        break;
      case EModuleType.FIREARM:
        setModuleLabel('manageImages.firearm.label');
        break;
      case EModuleType.ART:
        setModuleLabel('manageImages.ancillary.label');
        break;
      case EModuleType.ANCILLARY:
        setModuleLabel('manageImages.ancillary.label');
        break;
      default:
        setModuleLabel('');
        break;
    }
  }, [moduleType]);

  const [url, setUrl] = useState<string>('');

  const getPhotoThumbnail = async (imgId: string) => {
    const photo = await getGalleryPreview(
      imgId,
      moduleType === EModuleType.LE ? ESize.THUMBNAIL : ESize.THUMBNAIL_SQRT,
    );
    setImageThumbnails((prev) => ({
      ...prev,
      [imgId]: photo,
    }));
    return photo;
  };

  useEffect(() => {
    (async function getMainPhotoInit() {
      if (galleryData) {
        const img = galleryData.find((elem: IEntryData) => Number(elem.position) === 0);
        if (img && !imageThumbnails[img?.uniqueId]) {
          getPhotoThumbnail(img?.uniqueId);
        }
      }
    })();
  }, [galleryData]);

  useEffect(() => {
    (async function getPhotoDataInit() {
      setIsLoading(true);
      const galleryData = await getGallery(ownerId);
      if (galleryData) {
        if (init && galleryData.entries && galleryData.entries.length) {
          init();
        }
        setGalleryData(
          galleryData.entries.sort((a, b) => Number(a.position) - Number(b.position)),
        );
      }
      setIsLoading(false);
    })();
  }, []);

  const getNextPhotoThumbnail = (position: number) => {
    (async function getPhotoData() {
      const img = galleryData.find((elem: IEntryData) => Number(elem.position) === position);
      const imgId = img?.uniqueId;

      if (imgId && !imageThumbnails[imgId]) {
        await getPhotoThumbnail(imgId);
      }
    })();
  };

  useEffect(() => {
    (async function getPhotoData() {
      if (Object.keys(imageThumbnails).length) {
        const img = galleryData.find(
          (elem: IEntryData) => Number(elem.position) === currentPosition - 1,
        );
        const imgId = img?.uniqueId;

        if (imgId && imageThumbnails[imgId]) {
          setUrl(imageThumbnails[imgId]);
          setImageThumbnailFileIsLoading(false);
        } else if (imgId) {
          setImageThumbnailFileIsLoading(true);
          await getPhotoThumbnail(imgId);
        }
        getNextPhotoThumbnail(currentPosition);
      }
    })();
  }, [currentPosition, imageThumbnails]);

  const handlePrev = () => {
    if (currentPosition > 1) {
      setCurrentPosition(currentPosition - 1);
    }
  };
  const handleNext = () => {
    if (currentPosition < galleryData.length) {
      setCurrentPosition(currentPosition + 1);
    }
  };

  const handleFullSize = () => {
    setOpenFullSize(true);
  };

  if (!galleryData.length) {
    if (hidden) return '';
    return (
      <Grid container size={{ xs: 12 }} sx={{ rowGap: 1, alignSelf: 'start' }}>
        <StyledEmptyPhoto moduleType={moduleType}>
          {isLoading ? (
            <Loader isLoading />
          ) : (
            <>
              <IconStyled>
                <Icon
                  type={moduleType === EModuleType.LE ? EIconTypes.user : EIconTypes.camera}
                  color={colors.gray200}
                  fontSize={48}
                />
              </IconStyled>
              <Grid
                size={{ xs: 12 }}
                sx={{ gap: '4px', justifyContent: 'center', textAlign: 'center' }}
              >
                <Typography variant="subtitle2" sx={{ color: colors.gray900 }}>
                  {`${t('manageImages.emptyPhoto.label').replace('{0}', t(moduleLabel))}${moduleType === EModuleType.LE ? '*' : ''}`}
                </Typography>
                {showButton && (
                  <Typography
                    variant="body2"
                    sx={{ color: colors.gray400, fontStyle: 'italic' }}
                  >
                    {moduleType === EModuleType.LE
                      ? t('manageImages.pleaseAddPhotoRequiredLicence.label').replace(
                          '{0}',
                          t(moduleLabel),
                        )
                      : t('manageImages.pleaseAddPhoto.label').replace('{0}', t(moduleLabel))}
                  </Typography>
                )}
              </Grid>
            </>
          )}
        </StyledEmptyPhoto>
        {showButton && onClickAdd && (
          <Button
            id={buttonId}
            label={t('manageImages.addPhoto.label')}
            variant={EButtonVariants.outlined}
            permission={buttonPermission}
            size={EButtonSizes.small}
            fullWidth
            onClick={onClickAdd}
            disabled={isLoading}
            isLoading={isLoading}
          />
        )}
      </Grid>
    );
  }

  return (
    <>
      <ModalImages
        open={openFullSize}
        onClose={() => setOpenFullSize(false)}
        onPrev={() => handlePrev()}
        onNext={() => handleNext()}
        imageThumbnails={imageThumbnails}
        getPhotoThumbnail={getPhotoThumbnail}
        currentPosition={currentPosition}
        setCurrentPosition={setCurrentPosition}
        galleryData={galleryData}
        sizeFullImage={moduleType === EModuleType.LE ? ESize.FULL : ESize.FULL_SQRT}
      />

      {!hidden && (
        <div>
          <StyledPhoto moduleType={moduleType}>
            <GalleryCell moduleType={moduleType}>
              <GalleryContainer>
                <ZoomIcon className="zoom-icon">
                  <IconButton
                    onClick={handleFullSize}
                    sx={{
                      padding: 0,
                      width: '16px',
                      height: '16px',
                    }}
                  >
                    <Icon type={EIconTypes.fullScreen} fontSize={20} />
                  </IconButton>
                </ZoomIcon>
                {currentPosition === 1 && (
                  <MainPhotoChips>
                    <Chips
                      text={t('images.mainPhoto.label')}
                      variant={EChipsVariants.primary}
                    />
                  </MainPhotoChips>
                )}
                <ImagesGalleryContainer onClick={handleFullSize} moduleType={moduleType}>
                  <ImageContainer>
                    {imageThumbnailFileIsLoading || !url ? (
                      <Loader isLoading={true} />
                    ) : (
                      <img alt={`photo_${currentPosition}`} src={url} />
                    )}
                  </ImageContainer>
                </ImagesGalleryContainer>
                <ImagesGalleryControl>
                  <ControlButton onClick={handlePrev} disabled={currentPosition <= 1}>
                    <Icon type={EIconTypes.left} />
                  </ControlButton>
                  <ControlCounter>{`${currentPosition}/${galleryData.length}`}</ControlCounter>
                  <ControlButton
                    onClick={handleNext}
                    disabled={currentPosition >= galleryData.length}
                  >
                    <Icon type={EIconTypes.right} />
                  </ControlButton>
                </ImagesGalleryControl>
              </GalleryContainer>
            </GalleryCell>
          </StyledPhoto>
          {showButton && onClickEdit && (
            <Button
              id={buttonId}
              label={t('manageImages.editPhoto.label')}
              variant={EButtonVariants.outlined}
              permission={buttonPermission}
              size={EButtonSizes.small}
              fullWidth
              sx={{ mt: 2 }}
              onClick={onClickEdit}
              disabled={isLoading}
            />
          )}
        </div>
      )}
    </>
  );
};

export default ImagesGallery;
